// A REAL gateway render of the AI Modification Addendum, for the MOCK client and the parity tests only. Generated
// on 23 Oct 2026 from the gateway's seed (CodeWord-Back-NEW, branch feat/modification-addendum-click-through): the
// seeded types document, the seeded addendum template and Joey's field values, rendered with the fixed values in
// preview.values. The gateway pins the same render's hash (modificationAgreement.spec.ts, "cross-repo parity"), so
// this file cannot drift from what the server sends without one of the two tests failing.
//
// Young Hollywood's wording is in here because the server's answers are; the screens never import this file, and
// the no-hard-coding test keeps every one of these strings out of src/ outside the mock fixtures.
import type { AgreementDocument, ModificationType } from '../../models/modification.interface';

export const MOCK_AGREEMENT_RENDER: {
  modificationTypes: ModificationType[];
  modificationNotice: string;
  typesTemplate: { templateId: string; version: number; sha256: string };
  agreementTemplate: { templateId: string; version: number };
  limits: { instructionsMaxChars: number; productionMaxChars: number };
  /** The addendum template's own names for the two fields the buyer types (fields[3] and fields[4] of the template). */
  fieldLabels: { modificationInstructions: string; production: string };
  preview: {
    modificationTypeId: string;
    values: Record<string, string>;
    document: AgreementDocument;
    plainText: string;
    recordJson: string;
    renderedSha256: string;
    checkboxText: string;
    buttonLabel: string;
  };
} = {
  "modificationTypes": [
    {
      "id": "remove_object",
      "label": "Remove Object / Person",
      "description": "Remove a selected person, product, logo, sign, prop, or unwanted object from the footage and naturally reconstruct the area behind it.",
      "requiresApproval": true
    },
    {
      "id": "replace_object",
      "label": "Replace Object / Product",
      "description": "Swap an existing object, product, logo, sign, screen, or branded element with a different selected item while maintaining the original movement, lighting, and perspective.",
      "requiresApproval": true
    },
    {
      "id": "add_object",
      "label": "Add Object / Product",
      "description": "Insert a new product, prop, logo, sign, person, screen, or other visual element into the footage so it appears naturally within the scene.",
      "requiresApproval": true
    },
    {
      "id": "change_background",
      "label": "Change Background",
      "description": "Replace or transform the setting behind the main subject, including the location, environment, sky, set, weather, or time of day.",
      "requiresApproval": true
    },
    {
      "id": "reimagine_scene",
      "label": "Reimagine Entire Scene",
      "description": "Transform the full clip into a dramatically different visual environment or creative style while preserving the core subject or action,",
      "requiresApproval": true
    }
  ],
  "modificationNotice": "AI modifications are subject to the rights granted by the content owner. Some modifications may require content-owner approval before the modified clip can be downloaded or used.",
  "typesTemplate": {
    "templateId": "modification-types",
    "version": 1,
    "sha256": "9dcab412cfac8027ba740366513137a2573503dd786415ce5537e19dc09127b6"
  },
  "agreementTemplate": {
    "templateId": "ai-modification-addendum",
    "version": 1
  },
  "limits": {
    "instructionsMaxChars": 4000,
    "productionMaxChars": 200
  },
  "fieldLabels": {
    "modificationInstructions": "Modification Instructions",
    "production": "Production or Project"
  },
  "preview": {
    "modificationTypeId": "replace_object",
    "values": {
      "originalContent": "Mock clip title (ID 311624281fb846699e41e04859236db9)",
      "contentOwner": "Young Hollywood",
      "selectedModification": "Replace Object / Product",
      "modificationInstructions": "Mock instructions: replace the can on the table with a glass of water.",
      "production": "Mock production",
      "approvalStatus": "Approval Required",
      "modificationRequestId": "00000000-0000-4000-8000-0000000000aa"
    },
    "document": {
      "blocks": [
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "CODEWORD AI MODIFICATION ADDENDUM",
                "bold": true
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "This AI Modification Addendum (“Addendum”) supplements and forms part of the Codeword Content Licensing Agreement (“License Agreement”) accepted by the person or entity requesting the modification (“Licensee”).",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "This Addendum applies only to the modification request and modified output identified below.",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "Original Content:",
                "bold": true
              },
              {
                "text": " ",
                "bold": false
              },
              {
                "text": "Mock clip title (ID 311624281fb846699e41e04859236db9)",
                "bold": false,
                "field": "originalContent"
              }
            ],
            [
              {
                "text": "Content Owner:",
                "bold": true
              },
              {
                "text": " ",
                "bold": false
              },
              {
                "text": "Young Hollywood",
                "bold": false,
                "field": "contentOwner"
              }
            ],
            [
              {
                "text": "Selected Modification:",
                "bold": true
              },
              {
                "text": " ",
                "bold": false
              },
              {
                "text": "Replace Object / Product",
                "bold": false,
                "field": "selectedModification"
              }
            ],
            [
              {
                "text": "Modification Instructions:",
                "bold": true
              },
              {
                "text": " ",
                "bold": false
              },
              {
                "text": "Mock instructions: replace the can on the table with a glass of water.",
                "bold": false,
                "field": "modificationInstructions"
              }
            ],
            [
              {
                "text": "Production or Project:",
                "bold": true
              },
              {
                "text": " ",
                "bold": false
              },
              {
                "text": "Mock production",
                "bold": false,
                "field": "production"
              }
            ],
            [
              {
                "text": "Approval Status:",
                "bold": true
              },
              {
                "text": " ",
                "bold": false
              },
              {
                "text": "Approval Required",
                "bold": false,
                "field": "approvalStatus"
              }
            ],
            [
              {
                "text": "Modification Request ID:",
                "bold": true
              },
              {
                "text": " ",
                "bold": false
              },
              {
                "text": "00000000-0000-4000-8000-0000000000aa",
                "bold": false,
                "field": "modificationRequestId"
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "1. Limited Authorization",
                "bold": true
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "Subject to payment and any required content-owner approval, Licensee may use the modified content only as part of the Production and only within the Term, Territory, Licensed Media and permitted uses stated in the License Agreement and applicable order form.",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "This Addendum constitutes Codeword’s written authorization solely for the selected modification and the modified output generated under the Modification Request ID above. It does not authorize any other modification or expand any music, talent, trademark, publicity or other third-party rights.",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "2. Licensee Materials",
                "bold": true
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "Licensee represents and warrants that it owns or has obtained all necessary rights and permissions for every prompt, image, logo, trademark, product, file, reference or other material submitted in connection with the modification.",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "Licensee is responsible for the materials it submits and for the instructions it provides to Codeword.",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "3. Prohibited Uses",
                "bold": true
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "Licensee may not use the modified content:",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "bullet",
          "lines": [
            [
              {
                "text": "To falsely represent that an identifiable person said, did, used, supported or endorsed something;",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "bullet",
          "lines": [
            [
              {
                "text": "To materially alter an identifiable person’s words, voice, conduct, performance or identity without specific authorization;",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "bullet",
          "lines": [
            [
              {
                "text": "In any unlawful, fraudulent, deceptive, defamatory, negative, disparaging or intentionally misleading manner;",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "bullet",
          "lines": [
            [
              {
                "text": "To violate any copyright, trademark, publicity, privacy or other third-party right;",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "bullet",
          "lines": [
            [
              {
                "text": "As standalone stock footage or as a separately resold or sublicensed asset;",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "bullet",
          "lines": [
            [
              {
                "text": "To create a reusable digital replica, facial model, voice model or identity asset;",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "bullet",
          "lines": [
            [
              {
                "text": "To train, fine-tune or develop an artificial-intelligence model; or",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "bullet",
          "lines": [
            [
              {
                "text": "To remove or conceal required watermarks, attribution, provenance information or rights-management information.",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "Codeword may reject, block or remove any modification request or output that violates these restrictions.",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "4. Content-Owner Approval",
                "bold": true
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "If the approval status is listed as ",
                "bold": false
              },
              {
                "text": "Approval Required",
                "bold": true
              },
              {
                "text": ", the modified content is available for preview only. Licensee may not download, publish, distribute or otherwise use the modified content until Codeword marks it as ",
                "bold": false
              },
              {
                "text": "Approved",
                "bold": true
              },
              {
                "text": ".",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "Approval of one modified output does not constitute approval of any other output, prompt, modification or version.",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "5. Ownership",
                "bold": true
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "The content owner retains all rights in the original content and its underlying materials. Licensee receives only the limited right to use the approved modified content under the License Agreement and this Addendum.",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "Licensee retains its rights in any independently owned materials that it submitted, but receives no ownership interest in the original content or modified content.",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "6. Licensee Review and Responsibility",
                "bold": true
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "Licensee must review the modified content before using it and is responsible for ensuring that its final use complies with applicable laws, platform requirements and any disclosure obligations.",
                "bold": false
              }
            ]
          ]
        },
        {
          "type": "paragraph",
          "lines": [
            [
              {
                "text": "The indemnification, limitation of liability, governing law, arbitration and all other provisions of the License Agreement apply to the modified content. If this Addendum conflicts with the License Agreement, this Addendum controls only with respect to the specific authorized modification. All other provisions of the License Agreement remain unchanged.",
                "bold": false
              }
            ]
          ]
        }
      ]
    },
    "plainText": "CODEWORD AI MODIFICATION ADDENDUM\n\nThis AI Modification Addendum (“Addendum”) supplements and forms part of the Codeword Content Licensing Agreement (“License Agreement”) accepted by the person or entity requesting the modification (“Licensee”).\n\nThis Addendum applies only to the modification request and modified output identified below.\n\nOriginal Content: Mock clip title (ID 311624281fb846699e41e04859236db9)\nContent Owner: Young Hollywood\nSelected Modification: Replace Object / Product\nModification Instructions: Mock instructions: replace the can on the table with a glass of water.\nProduction or Project: Mock production\nApproval Status: Approval Required\nModification Request ID: 00000000-0000-4000-8000-0000000000aa\n\n1. Limited Authorization\n\nSubject to payment and any required content-owner approval, Licensee may use the modified content only as part of the Production and only within the Term, Territory, Licensed Media and permitted uses stated in the License Agreement and applicable order form.\n\nThis Addendum constitutes Codeword’s written authorization solely for the selected modification and the modified output generated under the Modification Request ID above. It does not authorize any other modification or expand any music, talent, trademark, publicity or other third-party rights.\n\n2. Licensee Materials\n\nLicensee represents and warrants that it owns or has obtained all necessary rights and permissions for every prompt, image, logo, trademark, product, file, reference or other material submitted in connection with the modification.\n\nLicensee is responsible for the materials it submits and for the instructions it provides to Codeword.\n\n3. Prohibited Uses\n\nLicensee may not use the modified content:\n\n- To falsely represent that an identifiable person said, did, used, supported or endorsed something;\n\n- To materially alter an identifiable person’s words, voice, conduct, performance or identity without specific authorization;\n\n- In any unlawful, fraudulent, deceptive, defamatory, negative, disparaging or intentionally misleading manner;\n\n- To violate any copyright, trademark, publicity, privacy or other third-party right;\n\n- As standalone stock footage or as a separately resold or sublicensed asset;\n\n- To create a reusable digital replica, facial model, voice model or identity asset;\n\n- To train, fine-tune or develop an artificial-intelligence model; or\n\n- To remove or conceal required watermarks, attribution, provenance information or rights-management information.\n\nCodeword may reject, block or remove any modification request or output that violates these restrictions.\n\n4. Content-Owner Approval\n\nIf the approval status is listed as Approval Required, the modified content is available for preview only. Licensee may not download, publish, distribute or otherwise use the modified content until Codeword marks it as Approved.\n\nApproval of one modified output does not constitute approval of any other output, prompt, modification or version.\n\n5. Ownership\n\nThe content owner retains all rights in the original content and its underlying materials. Licensee receives only the limited right to use the approved modified content under the License Agreement and this Addendum.\n\nLicensee retains its rights in any independently owned materials that it submitted, but receives no ownership interest in the original content or modified content.\n\n6. Licensee Review and Responsibility\n\nLicensee must review the modified content before using it and is responsible for ensuring that its final use complies with applicable laws, platform requirements and any disclosure obligations.\n\nThe indemnification, limitation of liability, governing law, arbitration and all other provisions of the License Agreement apply to the modified content. If this Addendum conflicts with the License Agreement, this Addendum controls only with respect to the specific authorized modification. All other provisions of the License Agreement remain unchanged.",
    "recordJson": "{\"agreement\":{\"templateId\":\"ai-modification-addendum\",\"templateSha256\":\"ddc272fcd804a3f30e9fd8ecc4712271fca54eb95b7da1125030c5d0d1e13336\",\"version\":1},\"buttonLabel\":\"Agree & Generate\",\"checkboxText\":\"I have reviewed and agree to the Codeword AI Modification Addendum. I confirm that I have all necessary rights to the materials I submitted and understand that I may use the modified content only under my Codeword Content License. I will not use it to falsely depict or imply a person’s words, actions, identity or endorsement.\",\"document\":{\"blocks\":[{\"lines\":[[{\"bold\":true,\"text\":\"CODEWORD AI MODIFICATION ADDENDUM\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":false,\"text\":\"This AI Modification Addendum (“Addendum”) supplements and forms part of the Codeword Content Licensing Agreement (“License Agreement”) accepted by the person or entity requesting the modification (“Licensee”).\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":false,\"text\":\"This Addendum applies only to the modification request and modified output identified below.\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":true,\"text\":\"Original Content:\"},{\"bold\":false,\"text\":\" \"},{\"bold\":false,\"field\":\"originalContent\",\"text\":\"Mock clip title (ID 311624281fb846699e41e04859236db9)\"}],[{\"bold\":true,\"text\":\"Content Owner:\"},{\"bold\":false,\"text\":\" \"},{\"bold\":false,\"field\":\"contentOwner\",\"text\":\"Young Hollywood\"}],[{\"bold\":true,\"text\":\"Selected Modification:\"},{\"bold\":false,\"text\":\" \"},{\"bold\":false,\"field\":\"selectedModification\",\"text\":\"Replace Object / Product\"}],[{\"bold\":true,\"text\":\"Modification Instructions:\"},{\"bold\":false,\"text\":\" \"},{\"bold\":false,\"field\":\"modificationInstructions\",\"text\":\"Mock instructions: replace the can on the table with a glass of water.\"}],[{\"bold\":true,\"text\":\"Production or Project:\"},{\"bold\":false,\"text\":\" \"},{\"bold\":false,\"field\":\"production\",\"text\":\"Mock production\"}],[{\"bold\":true,\"text\":\"Approval Status:\"},{\"bold\":false,\"text\":\" \"},{\"bold\":false,\"field\":\"approvalStatus\",\"text\":\"Approval Required\"}],[{\"bold\":true,\"text\":\"Modification Request ID:\"},{\"bold\":false,\"text\":\" \"},{\"bold\":false,\"field\":\"modificationRequestId\",\"text\":\"00000000-0000-4000-8000-0000000000aa\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":true,\"text\":\"1. Limited Authorization\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":false,\"text\":\"Subject to payment and any required content-owner approval, Licensee may use the modified content only as part of the Production and only within the Term, Territory, Licensed Media and permitted uses stated in the License Agreement and applicable order form.\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":false,\"text\":\"This Addendum constitutes Codeword’s written authorization solely for the selected modification and the modified output generated under the Modification Request ID above. It does not authorize any other modification or expand any music, talent, trademark, publicity or other third-party rights.\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":true,\"text\":\"2. Licensee Materials\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":false,\"text\":\"Licensee represents and warrants that it owns or has obtained all necessary rights and permissions for every prompt, image, logo, trademark, product, file, reference or other material submitted in connection with the modification.\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":false,\"text\":\"Licensee is responsible for the materials it submits and for the instructions it provides to Codeword.\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":true,\"text\":\"3. Prohibited Uses\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":false,\"text\":\"Licensee may not use the modified content:\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":false,\"text\":\"To falsely represent that an identifiable person said, did, used, supported or endorsed something;\"}]],\"type\":\"bullet\"},{\"lines\":[[{\"bold\":false,\"text\":\"To materially alter an identifiable person’s words, voice, conduct, performance or identity without specific authorization;\"}]],\"type\":\"bullet\"},{\"lines\":[[{\"bold\":false,\"text\":\"In any unlawful, fraudulent, deceptive, defamatory, negative, disparaging or intentionally misleading manner;\"}]],\"type\":\"bullet\"},{\"lines\":[[{\"bold\":false,\"text\":\"To violate any copyright, trademark, publicity, privacy or other third-party right;\"}]],\"type\":\"bullet\"},{\"lines\":[[{\"bold\":false,\"text\":\"As standalone stock footage or as a separately resold or sublicensed asset;\"}]],\"type\":\"bullet\"},{\"lines\":[[{\"bold\":false,\"text\":\"To create a reusable digital replica, facial model, voice model or identity asset;\"}]],\"type\":\"bullet\"},{\"lines\":[[{\"bold\":false,\"text\":\"To train, fine-tune or develop an artificial-intelligence model; or\"}]],\"type\":\"bullet\"},{\"lines\":[[{\"bold\":false,\"text\":\"To remove or conceal required watermarks, attribution, provenance information or rights-management information.\"}]],\"type\":\"bullet\"},{\"lines\":[[{\"bold\":false,\"text\":\"Codeword may reject, block or remove any modification request or output that violates these restrictions.\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":true,\"text\":\"4. Content-Owner Approval\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":false,\"text\":\"If the approval status is listed as \"},{\"bold\":true,\"text\":\"Approval Required\"},{\"bold\":false,\"text\":\", the modified content is available for preview only. Licensee may not download, publish, distribute or otherwise use the modified content until Codeword marks it as \"},{\"bold\":true,\"text\":\"Approved\"},{\"bold\":false,\"text\":\".\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":false,\"text\":\"Approval of one modified output does not constitute approval of any other output, prompt, modification or version.\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":true,\"text\":\"5. Ownership\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":false,\"text\":\"The content owner retains all rights in the original content and its underlying materials. Licensee receives only the limited right to use the approved modified content under the License Agreement and this Addendum.\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":false,\"text\":\"Licensee retains its rights in any independently owned materials that it submitted, but receives no ownership interest in the original content or modified content.\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":true,\"text\":\"6. Licensee Review and Responsibility\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":false,\"text\":\"Licensee must review the modified content before using it and is responsible for ensuring that its final use complies with applicable laws, platform requirements and any disclosure obligations.\"}]],\"type\":\"paragraph\"},{\"lines\":[[{\"bold\":false,\"text\":\"The indemnification, limitation of liability, governing law, arbitration and all other provisions of the License Agreement apply to the modified content. If this Addendum conflicts with the License Agreement, this Addendum controls only with respect to the specific authorized modification. All other provisions of the License Agreement remain unchanged.\"}]],\"type\":\"paragraph\"}]},\"modificationNotice\":\"AI modifications are subject to the rights granted by the content owner. Some modifications may require content-owner approval before the modified clip can be downloaded or used.\",\"modificationType\":{\"description\":\"Swap an existing object, product, logo, sign, screen, or branded element with a different selected item while maintaining the original movement, lighting, and perspective.\",\"id\":\"replace_object\",\"label\":\"Replace Object / Product\"},\"plainText\":\"CODEWORD AI MODIFICATION ADDENDUM\\n\\nThis AI Modification Addendum (“Addendum”) supplements and forms part of the Codeword Content Licensing Agreement (“License Agreement”) accepted by the person or entity requesting the modification (“Licensee”).\\n\\nThis Addendum applies only to the modification request and modified output identified below.\\n\\nOriginal Content: Mock clip title (ID 311624281fb846699e41e04859236db9)\\nContent Owner: Young Hollywood\\nSelected Modification: Replace Object / Product\\nModification Instructions: Mock instructions: replace the can on the table with a glass of water.\\nProduction or Project: Mock production\\nApproval Status: Approval Required\\nModification Request ID: 00000000-0000-4000-8000-0000000000aa\\n\\n1. Limited Authorization\\n\\nSubject to payment and any required content-owner approval, Licensee may use the modified content only as part of the Production and only within the Term, Territory, Licensed Media and permitted uses stated in the License Agreement and applicable order form.\\n\\nThis Addendum constitutes Codeword’s written authorization solely for the selected modification and the modified output generated under the Modification Request ID above. It does not authorize any other modification or expand any music, talent, trademark, publicity or other third-party rights.\\n\\n2. Licensee Materials\\n\\nLicensee represents and warrants that it owns or has obtained all necessary rights and permissions for every prompt, image, logo, trademark, product, file, reference or other material submitted in connection with the modification.\\n\\nLicensee is responsible for the materials it submits and for the instructions it provides to Codeword.\\n\\n3. Prohibited Uses\\n\\nLicensee may not use the modified content:\\n\\n- To falsely represent that an identifiable person said, did, used, supported or endorsed something;\\n\\n- To materially alter an identifiable person’s words, voice, conduct, performance or identity without specific authorization;\\n\\n- In any unlawful, fraudulent, deceptive, defamatory, negative, disparaging or intentionally misleading manner;\\n\\n- To violate any copyright, trademark, publicity, privacy or other third-party right;\\n\\n- As standalone stock footage or as a separately resold or sublicensed asset;\\n\\n- To create a reusable digital replica, facial model, voice model or identity asset;\\n\\n- To train, fine-tune or develop an artificial-intelligence model; or\\n\\n- To remove or conceal required watermarks, attribution, provenance information or rights-management information.\\n\\nCodeword may reject, block or remove any modification request or output that violates these restrictions.\\n\\n4. Content-Owner Approval\\n\\nIf the approval status is listed as Approval Required, the modified content is available for preview only. Licensee may not download, publish, distribute or otherwise use the modified content until Codeword marks it as Approved.\\n\\nApproval of one modified output does not constitute approval of any other output, prompt, modification or version.\\n\\n5. Ownership\\n\\nThe content owner retains all rights in the original content and its underlying materials. Licensee receives only the limited right to use the approved modified content under the License Agreement and this Addendum.\\n\\nLicensee retains its rights in any independently owned materials that it submitted, but receives no ownership interest in the original content or modified content.\\n\\n6. Licensee Review and Responsibility\\n\\nLicensee must review the modified content before using it and is responsible for ensuring that its final use complies with applicable laws, platform requirements and any disclosure obligations.\\n\\nThe indemnification, limitation of liability, governing law, arbitration and all other provisions of the License Agreement apply to the modified content. If this Addendum conflicts with the License Agreement, this Addendum controls only with respect to the specific authorized modification. All other provisions of the License Agreement remain unchanged.\",\"schema\":\"ai-addendum-render/1\",\"types\":{\"sha256\":\"9dcab412cfac8027ba740366513137a2573503dd786415ce5537e19dc09127b6\",\"templateId\":\"modification-types\",\"version\":1}}",
    "renderedSha256": "fbef6516b21fc29925848563b01d860235931498ca4012d4343f49ed16c18bf0",
    "checkboxText": "I have reviewed and agree to the Codeword AI Modification Addendum. I confirm that I have all necessary rights to the materials I submitted and understand that I may use the modified content only under my Codeword Content License. I will not use it to falsely depict or imply a person’s words, actions, identity or endorsement.",
    "buttonLabel": "Agree & Generate"
  }
};
